import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import OnScreenKeyboard from "../../components/common/OnScreenKeyboard";
import {
  useAssignReservationTableMutation,
  useCreateReservationMutation,
  useGetReservationsQuery,
  useGetEdgeCustomersQuery,
  useGetReservationSlotsQuery,
  useGetTablesQuery,
  useUpdateReservationMutation,
} from "../../store/api";
import {
  validateCustomerName,
  validateCustomerPhone,
} from "../../utils/customerValidation";
import { getErrorMessage } from "../../utils/errorMessage";

const today = new Date().toISOString().slice(0, 10);

const weekDays = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const iconClassName = "h-5 w-5";
const normalizeGuestMobile = (value) => String(value || "").replace(/\D/g, "").slice(-10);

const parseIsoDate = (isoDate) => {
  if (!isoDate) return null;
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const ReservationDateInput = forwardRef(({ value, onClick, disabled }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex h-11 w-full items-center rounded-lg border border-slate-200 bg-white px-4 text-left text-base font-normal text-slate-900 shadow-sm outline-none transition hover:border-indigo-300 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100"
  >
    {value || "Select date"}
  </button>
));

ReservationDateInput.displayName = "ReservationDateInput";

const ArrowLeftIcon = ({ className = iconClassName }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ArrowRightIcon = ({ className = iconClassName }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ClockIcon = ({ className = iconClassName }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ListIcon = ({ className = iconClassName }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
    />
  </svg>
);

const DotIcon = ({ className = "h-2.5 w-2.5" }) => (
  <svg className={className} viewBox="0 0 8 8" fill="currentColor">
    <circle cx="4" cy="4" r="4" />
  </svg>
);

const pageShellClass =
  "mx-auto flex w-full max-w-[1600px] flex-col gap-3 px-4 pb-4 pt-3 sm:px-6 sm:pt-4 lg:px-8 lg:gap-3 lg:pt-4";

const ReservationsHeader = ({ title, subtitle, actions, compact = false, titleClassName = "" }) => {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="space-y-1.5">
        <h1
          className={[
            "font-semibold tracking-[-0.03em] text-slate-900 dark:text-slate-100",
            compact ? "text-[21px] sm:text-[24px]" : "text-[22px] sm:text-[26px] lg:text-[28px]",
            titleClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {title}
        </h1>
        {subtitle ? (
          <p
            className={[
              "max-w-2xl leading-6 text-slate-600 dark:text-slate-400",
              compact ? "text-[12px]" : "text-[13px]",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-3 lg:justify-end">{actions}</div> : null}
    </div>
  );
};

const WarningIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M8.257 3.099c.765-1.36 2.72-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.986-1.742 2.986H3.48c-1.53 0-2.492-1.652-1.743-2.986l6.52-11.59zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-6a1 1 0 00-1 1v3a1 1 0 102 0V8a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

const XCircleIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-10.293a1 1 0 00-1.414-1.414L10 8.586 7.707 6.293a1 1 0 00-1.414 1.414L8.586 10l-2.293 2.293a1 1 0 101.414 1.414L10 11.414l2.293 2.293a1 1 0 001.414-1.414L11.414 10l2.293-2.293z"
      clipRule="evenodd"
    />
  </svg>
);

const toDisplayTime = (value) => {
  if (!value) return "";
  const [hours = "00", minutes = "00"] = value.split(":");
  const parsed = new Date();
  parsed.setHours(Number(hours), Number(minutes), 0, 0);
  return parsed.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const toIsoDate = (dateObj) => {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatLongDate = (isoDate) => {
  if (!isoDate) return "";
  const parsed = new Date(`${isoDate}T00:00:00`);
  return parsed.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const getSlotState = (slotMeta, booked) => {
  if (!slotMeta?.isOpen) {
    return {
      tone: "unavailable",
      label: "Unavailable",
      detail: "Closed",
    };
  }

  const availableSeats = Number(slotMeta?.availableSeats ?? 0);
  const bookedSeats = Number(slotMeta?.bookedSeats ?? booked ?? 0);
  const totalSeats = Number(
    slotMeta?.totalSeatCapacity ??
      slotMeta?.totalSeats ??
      slotMeta?.capacity ??
      availableSeats + bookedSeats,
  );
  const remainingRatio = totalSeats > 0 ? availableSeats / totalSeats : 0;

  if (availableSeats <= 0) {
    return {
      tone: "full",
      label: "Fully Booked",
      detail: "No slots left",
    };
  }

  if (bookedSeats > 0 && remainingRatio <= 0.4) {
    return {
      tone: "limited",
      label: `${bookedSeats}${totalSeats ? ` / ${totalSeats}` : ""} slots`,
      detail: "Limited",
    };
  }

  return {
    tone: "available",
    label: "Available",
    detail: bookedSeats > 0 ? `${bookedSeats} booked` : "Open slot",
  };
};

const slotToneClasses = {
  available:
    "border-emerald-300 bg-white text-slate-900 hover:border-emerald-400 hover:bg-emerald-50/40",
  limited:
    "border-amber-300 bg-white text-slate-900 hover:border-amber-400 hover:bg-amber-50/40",
  full:
    "border-rose-300 bg-rose-50/60 text-slate-900 hover:border-rose-400",
  unavailable:
    "border-slate-200 bg-slate-100/70 text-slate-400",
};

const slotIndicatorClasses = {
  available: "text-emerald-500",
  limited: "text-amber-500",
  full: "text-rose-500",
  unavailable: "text-slate-400",
};

const ReservationSlotsLoadingState = () => (
  <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-slate-50 px-4 py-5">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div
        className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600"
        role="progressbar"
        aria-label="Loading reservation slots"
        aria-valuetext="Loading reservation slots"
        aria-busy="true"
      />
      <div className="space-y-1">
        <div className="text-sm font-semibold text-slate-900">Loading reservation slots</div>
        <p className="text-sm text-slate-600">
          Please wait while we fetch the latest slot availability for the selected date.
        </p>
      </div>
    </div>
  </div>
);

const dayToneClasses = {
  available: "border-slate-200 bg-white text-slate-900 hover:border-indigo-300 hover:text-indigo-600",
  selected: "border-transparent bg-gradient-to-br from-indigo-600 to-violet-500 text-white shadow-[0_18px_36px_rgba(79,70,229,0.35)]",
  inactive: "border-transparent bg-transparent text-transparent shadow-none pointer-events-none",
};

const initialFormState = {
  reservationDate: today,
  slotTime: "",
  guestCount: "",
  customerName: "",
  mobile: "",
  email: "",
};

const buildReservationDraft = (reservation) => ({
  reservationId: reservation?.reservationId || null,
  reservationDate: reservation?.reservationDate || today,
  slotTime: reservation?.slotTime || "",
  guestCount:
    reservation?.guestCount === 0 || reservation?.guestCount
      ? String(reservation.guestCount)
      : "",
  customerName: reservation?.customerName || "",
  mobile: reservation?.mobile || reservation?.mobileE164 || "",
  email: reservation?.email || "",
  reservationCode: reservation?.reservationCode || "",
});

const getReservationId = (reservation) =>
  reservation?.reservationId || reservation?.id || reservation?.reservationID || null;

const getTableId = (table) => table?.tableId || table?.id || null;

const getTableLabel = (table) =>
  table?.displayName || table?.tableNo || table?.tableNumber || table?.name || "Table";

const getAssignTableBackground = (table) => table?.backgroundColor || "#ffffff";

const isTableAssignable = (table, currentTableId) => {
  if (!table || table.isActive === false) return false;

  const tableId = getTableId(table);
  if (!tableId) return false;
  if (currentTableId && tableId && String(tableId) === String(currentTableId)) {
    return true;
  }

  const status = String(table.status || "AVAILABLE").toUpperCase();
  return !status || status === "AVAILABLE";
};

const Reservations = () => {
  const [step, setStep] = useState(1);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedDate, setSelectedDate] = useState(today);
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [createdReservation, setCreatedReservation] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showGuestKeyboardModal, setShowGuestKeyboardModal] = useState(false);
  const [showReservationConfirmModal, setShowReservationConfirmModal] = useState(false);
  const [activeGuestField, setActiveGuestField] = useState("mobile");
  const [isAutoFilledCustomer, setIsAutoFilledCustomer] = useState(false);
  const [editingReservationId, setEditingReservationId] = useState(null);
  const [showReservationsList, setShowReservationsList] = useState(false);
  const [reservationOverlayContext, setReservationOverlayContext] = useState("slots");
  const [listStartDate, setListStartDate] = useState("");
  const [listEndDate, setListEndDate] = useState("");
  const [listStatus, setListStatus] = useState("");
  const [listSearch, setListSearch] = useState("");
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [listDateError, setListDateError] = useState("");
  const [step1Error, setStep1Error] = useState("");
  const [step2Error, setStep2Error] = useState("");
  const [step2FieldErrors, setStep2FieldErrors] = useState({});
  const [assigningReservation, setAssigningReservation] = useState(null);
  const [selectedAssignTableId, setSelectedAssignTableId] = useState("");
  const [assignTableError, setAssignTableError] = useState("");
  const [reservationListNotice, setReservationListNotice] = useState(null);

  const [form, setForm] = useState(initialFormState);
  const mobileInputRef = useRef(null);

  const closeReservationConfirmModal = () => {
    setShowReservationConfirmModal(false);
  };

  const { data } = useGetReservationsQuery({ date: selectedDate });
  const {
    data: slotsData,
    isLoading: isLoadingSlots,
    isFetching: isFetchingSlots,
  } = useGetReservationSlotsQuery(
    { date: selectedDate },
    { skip: !selectedDate },
  );
  const mobileLookupValue = normalizeGuestMobile(form.mobile);
  const { data: customerLookupData, isFetching: isLookingUpCustomer } = useGetEdgeCustomersQuery({
    search: mobileLookupValue || undefined,
    page: 1,
    pageSize: 25,
  }, {
    skip: !showGuestKeyboardModal || mobileLookupValue.length !== 10,
  });
  const {
    data: allReservationsData,
    isFetching: isRefreshingReservations,
  } = useGetReservationsQuery({
    status: listStatus || undefined,
    search: listSearch || undefined,
    refreshKey: listRefreshKey,
  });
  const [createReservation, createState] = useCreateReservationMutation();
  const [updateReservation, updateState] = useUpdateReservationMutation();
  const [assignReservationTable, assignTableState] = useAssignReservationTableMutation();
  const {
    data: tablesData,
    isLoading: isLoadingTables,
    isFetching: isFetchingTables,
  } = useGetTablesQuery(undefined, {
    skip: !assigningReservation,
    refetchOnMountOrArgChange: true,
  });

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const timeSlots = useMemo(() => {
    if (Array.isArray(slotsData) && slotsData.length > 0) {
      return slotsData
        .filter(
          (slot) => slot?.isOpen,
        )
        .map((slot) => slot.slotTime)
        .sort();
    }
    return [];
  }, [slotsData]);
  const isSlotsPending = Boolean(selectedDate) && (isLoadingSlots || isFetchingSlots);

  const bookedCountBySlot = useMemo(() => {
    if (Array.isArray(slotsData) && slotsData.length > 0) {
      const map = new Map();
      for (const slot of slotsData) map.set(slot.slotTime, slot.bookedSeats || 0);
      return map;
    }
    const map = new Map();
    for (const row of rows) {
      const key = row?.slotTime;
      if (!key) continue;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [rows, slotsData]);

  const slotByTime = useMemo(() => {
    if (!Array.isArray(slotsData) || slotsData.length === 0) return new Map();
    const map = new Map();
    for (const slot of slotsData) {
      if (slot?.slotTime) map.set(slot.slotTime, slot);
    }
    return map;
  }, [slotsData]);

  const selectedStep2SlotMeta = useMemo(
    () => slotByTime.get(form.slotTime || selectedTime) || null,
    [form.slotTime, selectedTime, slotByTime],
  );
  const selectedStep2SlotState = useMemo(
    () => getSlotState(selectedStep2SlotMeta, bookedCountBySlot.get(form.slotTime || selectedTime) || 0),
    [bookedCountBySlot, form.slotTime, selectedStep2SlotMeta, selectedTime],
  );
  const selectedStep2AvailableSeats = Number(selectedStep2SlotMeta?.availableSeats ?? 0);
  const guestFieldMeta = {
    mobile: {
      label: "Mobile",
      placeholder: "Enter mobile number",
      layout: "numeric",
    },
    customerName: {
      label: "Customer Name",
      placeholder: "Enter customer name",
      layout: "alpha",
    },
    email: {
      label: "Email",
      placeholder: "Enter email address",
      layout: "alpha",
    },
    guestCount: {
      label: "Guests",
      placeholder: "Enter guest count",
      layout: "numeric",
    },
  };
  const matchedCustomerByMobile = useMemo(() => {
    const items = Array.isArray(customerLookupData?.customers) ? customerLookupData.customers : [];
    return items.find((customer) => normalizeGuestMobile(customer?.mobileE164 || customer?.mobile || "") === mobileLookupValue) || null;
  }, [customerLookupData, mobileLookupValue]);
  const allReservations = useMemo(
    () => (Array.isArray(allReservationsData) ? allReservationsData : []),
    [allReservationsData],
  );
  const allTables = useMemo(
    () => (Array.isArray(tablesData) ? tablesData : []),
    [tablesData],
  );
  const currentAssignedTableId =
    assigningReservation?.tableId ||
    assigningReservation?.assignedTableId ||
    assigningReservation?.table?.tableId ||
    "";
  const assignableTables = useMemo(() => {
    return allTables
      .filter((table) => isTableAssignable(table, currentAssignedTableId))
      .sort((left, right) => getTableLabel(left).localeCompare(getTableLabel(right)));
  }, [allTables, currentAssignedTableId]);

  const filteredReservations = useMemo(() => {
    const hasStart = Boolean(listStartDate);
    const hasEnd = Boolean(listEndDate);

    if (!hasStart && !hasEnd) return allReservations;

    return allReservations.filter((reservation) => {
      const reservationDate = reservation?.reservationDate;
      if (!reservationDate) return false;
      if (hasStart && reservationDate < listStartDate) return false;
      if (hasEnd && reservationDate > listEndDate) return false;
      return true;
    });
  }, [allReservations, listEndDate, listStartDate]);

  const handleListStartDateChange = (nextValue) => {
    if (!nextValue) {
      setListStartDate("");
      setListDateError("");
      return;
    }

    setListStartDate(nextValue);
    setListDateError("");

    if (listEndDate && nextValue > listEndDate) {
      setListEndDate(nextValue);
    }
  };

  const handleListEndDateChange = (nextValue) => {
    if (!nextValue) {
      setListEndDate("");
      setListDateError("");
      return;
    }

    if (listStartDate && nextValue < listStartDate) {
      setListDateError("To date cannot be earlier than From date.");
      return;
    }

    setListEndDate(nextValue);
    setListDateError("");
  };

  const calendarModel = useMemo(() => {
    const base = new Date();
    base.setDate(1);
    base.setMonth(base.getMonth() + monthOffset);

    const year = base.getFullYear();
    const month = base.getMonth();
    const monthLabel = base.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const jsDay = first.getDay();
    const mondayBased = (jsDay + 6) % 7;

    const cells = [];
    for (let i = 0; i < mondayBased; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateObj = new Date(year, month, day);
      cells.push({ day, iso: toIsoDate(dateObj) });
    }

    while (cells.length % 7 !== 0) cells.push(null);

    return { monthLabel, cells };
  }, [monthOffset]);

  const selectedDayLabel = useMemo(() => formatLongDate(selectedDate), [selectedDate]);
  const modalDateLabel = useMemo(
    () => formatLongDate(form.reservationDate || selectedDate),
    [form.reservationDate, selectedDate],
  );
  const shouldShowReservationsList =
    showReservationsList || ((showGuestKeyboardModal || showSuccessModal) && reservationOverlayContext === "list");

  const openReservationEditor = (reservation) => {
    const draft = buildReservationDraft(reservation);
    const isOpeningFromList = showReservationsList;
    setReservationOverlayContext(isOpeningFromList ? "list" : "slots");
    setEditingReservationId(draft.reservationId);
    setSelectedDate(draft.reservationDate);
    setSelectedTime(draft.slotTime);
    setIsAutoFilledCustomer(false);
    setSelectedCustomerOption(null);
    setShowSuccessModal(false);
    setShowGuestKeyboardModal(false);
    setStep1Error("");
    setStep2Error("");
    setShowReservationsList(isOpeningFromList);
    setForm({
      reservationDate: draft.reservationDate,
      slotTime: draft.slotTime,
      guestCount: draft.guestCount,
      customerName: draft.customerName,
      mobile: normalizeGuestMobile(draft.mobile),
      email: draft.email,
    });
    setActiveGuestField("mobile");
    setShowGuestKeyboardModal(true);
  };

  const openAssignTableModal = (reservation) => {
    const existingTableId =
      reservation?.tableId ||
      reservation?.assignedTableId ||
      reservation?.table?.tableId ||
      "";
    setReservationListNotice(null);
    setAssignTableError("");
    setAssigningReservation(reservation);
    setSelectedAssignTableId(existingTableId || "");
  };

  const closeAssignTableModal = () => {
    if (assignTableState.isLoading) return;
    setAssigningReservation(null);
    setSelectedAssignTableId("");
    setAssignTableError("");
  };

  const handleConfirmAssignTable = async () => {
    const reservationId = getReservationId(assigningReservation);
    if (!reservationId) {
      setAssignTableError("Reservation id was not found.");
      return;
    }
    if (!selectedAssignTableId) {
      setAssignTableError("Please select an available table.");
      return;
    }

    try {
      await assignReservationTable({
        reservationId,
        tableId: selectedAssignTableId,
      }).unwrap();

      const selectedTable = allTables.find(
        (table) => String(getTableId(table)) === String(selectedAssignTableId),
      );
      setReservationListNotice({
        tone: "success",
        message: `Table ${getTableLabel(selectedTable)} assigned successfully.`,
      });
      setAssigningReservation(null);
      setSelectedAssignTableId("");
      setAssignTableError("");
      setListRefreshKey((prev) => prev + 1);
    } catch (err) {
      setAssignTableError(getErrorMessage(err, "Failed to assign table. Please try again."));
    }
  };

  const startDetailsStep = (slotTimeOverride = selectedTime) => {
    setStep1Error("");
    setStep2Error("");
    setStep2FieldErrors({});

    if (!selectedDate) {
      setStep1Error("Please select a reservation date.");
      return;
    }

    if (!slotTimeOverride || !timeSlots.includes(slotTimeOverride)) {
      setStep1Error("Please select a valid time slot before continuing.");
      return;
    }

    const selectedSlot = slotByTime.get(slotTimeOverride);
    if (selectedSlot && Number(selectedSlot.availableSeats || 0) <= 0) {
      setStep1Error("Selected slot is fully booked. Please choose another slot.");
      return;
    }

    if (!editingReservationId) {
      setIsAutoFilledCustomer(false);
      setSelectedCustomerOption(null);
    }

    setForm((prev) => ({
      ...prev,
      reservationDate: selectedDate,
      slotTime: slotTimeOverride,
      guestCount: editingReservationId ? prev.guestCount : "",
      customerName: editingReservationId ? prev.customerName : "",
      mobile: editingReservationId ? prev.mobile : "",
      email: editingReservationId ? prev.email : "",
    }));
    setActiveGuestField("mobile");
    setShowGuestKeyboardModal(true);
  };

  const getReservationPayload = () => {
    setStep2Error("");
    setStep2FieldErrors({});

    const trimmedName = (form.customerName || "").trim();
    const trimmedMobile = (form.mobile || "").trim();
    const trimmedEmail = (form.email || "").trim();
    const guestCount = Number(form.guestCount || 0);

    if (!form.reservationDate || !form.slotTime) {
      setStep2Error("Please select date and time.");
      return null;
    }

    const nextFieldErrors = {};
    const mobileError = validateCustomerPhone(trimmedMobile, { required: true });
    if (mobileError) {
      nextFieldErrors.mobile = mobileError;
    }

    const nameError = validateCustomerName(trimmedName, { required: true });
    if (nameError) {
      nextFieldErrors.customerName = nameError;
    }

    if (!guestCount || guestCount < 1) {
      nextFieldErrors.guestCount = "Guest count must be at least 1.";
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setStep2FieldErrors(nextFieldErrors);
      setActiveGuestField(
        nextFieldErrors.mobile
          ? "mobile"
          : nextFieldErrors.customerName
            ? "customerName"
            : "guestCount",
      );
      return null;
    }

    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStep2Error("Enter a valid email address.");
      return null;
    }

    return {
      reservationDate: form.reservationDate,
      slotTime: form.slotTime,
      guestCount,
      customerName: trimmedName,
      mobile: trimmedMobile,
      email: trimmedEmail || null,
    };
  };

  const handleCreateOrUpdate = async () => {
    const basePayload = getReservationPayload();
    if (!basePayload) {
      return;
    }

    if (!editingReservationId && !showReservationConfirmModal) {
      setShowReservationConfirmModal(true);
      return;
    }

    try {
      const created = editingReservationId
        ? await updateReservation({
            reservationId: editingReservationId,
            payload: basePayload,
          }).unwrap()
        : await createReservation({
            ...basePayload,
            source: "EDGE_POS",
            isPaid: false,
            paymentType: "NONE",
            bookingAmount: 0,
          }).unwrap();

      setCreatedReservation({
        ...created,
        ...basePayload,
        reservationId: created?.reservationId || editingReservationId || null,
      });
      setShowReservationConfirmModal(false);
      setShowGuestKeyboardModal(false);
      setShowSuccessModal(true);
      setEditingReservationId(null);
    } catch (err) {
      const detail = err?.data?.detail || "Selected slot does not have enough available seats.";
      setStep2Error(detail);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setCreatedReservation(null);
    setShowSuccessModal(false);
    setShowGuestKeyboardModal(false);
    setShowReservationConfirmModal(false);
    setIsAutoFilledCustomer(false);
    setEditingReservationId(null);
    setSelectedCustomerOption(null);
    setSelectedTime("");
    setReservationOverlayContext("slots");
    setStep1Error("");
    setStep2Error("");
    setStep2FieldErrors({});
    setForm(initialFormState);
  };

  const clearReservationEditorState = () => {
    setEditingReservationId(null);
    setIsAutoFilledCustomer(false);
    setSelectedCustomerOption(null);
    setActiveGuestField("mobile");
    setStep2Error("");
    setStep2FieldErrors({});
    setForm((prev) => ({
      ...initialFormState,
      reservationDate: prev?.reservationDate || selectedDate || today,
      slotTime: "",
    }));
  };

  const getActiveGuestValue = () => {
    if (activeGuestField === "guestCount") {
      return String(form.guestCount ?? "");
    }
    return String(form[activeGuestField] ?? "");
  };

  const updateGuestFieldValue = (field, nextValue) => {
    if (field === "mobile") {
      const sanitized = normalizeGuestMobile(nextValue);
      setStep2Error("");
      setStep2FieldErrors((prev) => ({ ...prev, mobile: "" }));
      if (sanitized.length < 10 && isAutoFilledCustomer) {
        setForm((prev) => ({
          ...prev,
          mobile: sanitized,
          customerName: "",
          email: "",
        }));
        setIsAutoFilledCustomer(false);
        setSelectedCustomerOption(null);
        return;
      }
      setSelectedCustomerOption(null);
      setForm((prev) => ({ ...prev, mobile: sanitized }));
      return;
    }

    if (field === "guestCount") {
      const digitsOnly = String(nextValue || "").replace(/\D/g, "");
      setStep2FieldErrors((prev) => ({ ...prev, guestCount: "" }));
      setStep2Error("");
      setForm((prev) => ({ ...prev, guestCount: digitsOnly }));
      return;
    }

    setStep2FieldErrors((prev) => ({ ...prev, [field]: "" }));
    setStep2Error("");
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const setActiveGuestValue = (nextValue) => {
    updateGuestFieldValue(activeGuestField, nextValue);
  };

  const handleReservationDateChange = (nextDate) => {
    if (!editingReservationId || !nextDate) return;
    const nextIsoDate = toIsoDate(nextDate);
    setSelectedDate(nextIsoDate);
    setSelectedTime("");
    setStep2Error("");
    setForm((prev) => ({
      ...prev,
      reservationDate: nextIsoDate,
      slotTime: "",
    }));
  };

  const handleReservationTimeChange = (nextSlotTime) => {
    if (!editingReservationId) return;
    setSelectedTime(nextSlotTime);
    setStep2Error("");
    setForm((prev) => ({
      ...prev,
      slotTime: nextSlotTime,
    }));
  };

  const validateMobileBeforeFieldSwitch = (nextField) => {
    if (nextField === "mobile") return;
    const mobileError = validateCustomerPhone(form.mobile, { required: true });
    setStep2FieldErrors((prev) => ({
      ...prev,
      mobile: mobileError || "",
    }));
    return !mobileError;
  };

  const focusGuestField = (field) => {
    if (field !== "mobile" && !validateMobileBeforeFieldSwitch(field)) {
      setActiveGuestField("mobile");
      mobileInputRef.current?.focus();
      return;
    }
    setActiveGuestField(field);
  };

  const blockGuestFieldSwitch = (event, field) => {
    if (field === "mobile") return false;
    if (validateMobileBeforeFieldSwitch(field)) return false;
    event.preventDefault();
    event.stopPropagation();
    setActiveGuestField("mobile");
    mobileInputRef.current?.focus();
    return true;
  };

  const handleGuestKeyboardDone = () => {
    if (activeGuestField === "mobile") {
      const mobileError = validateCustomerPhone(form.mobile, { required: true });
      if (mobileError) {
        setStep2FieldErrors((prev) => ({ ...prev, mobile: mobileError }));
        return;
      }
      setActiveGuestField("customerName");
      return;
    }
    if (activeGuestField === "customerName") {
      const nameError = validateCustomerName(form.customerName, { required: true });
      if (nameError) {
        setStep2FieldErrors((prev) => ({ ...prev, customerName: nameError }));
        return;
      }
      setActiveGuestField("email");
      return;
    }
    if (activeGuestField === "email") {
      setActiveGuestField("guestCount");
      return;
    }
    if (!Number(form.guestCount || 0)) {
      setStep2FieldErrors((prev) => ({
        ...prev,
        guestCount: "Guest count must be at least 1.",
      }));
      return;
    }
    setShowGuestKeyboardModal(false);
  };

  useEffect(() => {
    if (!showGuestKeyboardModal) return;
    setActiveGuestField("mobile");
  }, [showGuestKeyboardModal]);

  useEffect(() => {
    if (!showGuestKeyboardModal || mobileLookupValue.length !== 10 || isLookingUpCustomer) return;

    if (matchedCustomerByMobile) {
      const nextName = matchedCustomerByMobile.fullName || matchedCustomerByMobile.customerName || "";
      const nextEmail = matchedCustomerByMobile.email || "";
      const matchedCustomerId = matchedCustomerByMobile.customerId || matchedCustomerByMobile.id || null;
      const matchedCustomerMobile = matchedCustomerByMobile.mobileE164 || matchedCustomerByMobile.mobile || "";
      const isSameAutoFilledCustomer =
        isAutoFilledCustomer &&
        selectedCustomerOption?.customerId === matchedCustomerId &&
        normalizeGuestMobile(selectedCustomerOption?.mobile || "") === mobileLookupValue;

      if (!isSameAutoFilledCustomer) {
        setIsAutoFilledCustomer(true);
        setSelectedCustomerOption({
          customerId: matchedCustomerId,
          customerName: nextName,
          mobile: matchedCustomerMobile,
          email: nextEmail,
        });
        setForm((prev) => ({
          ...prev,
          customerName: nextName || prev.customerName,
          email: nextEmail || prev.email,
        }));
        setActiveGuestField(nextName && nextEmail ? "guestCount" : nextName ? "email" : "customerName");
      }
      return;
    }

    if (isAutoFilledCustomer) {
      setForm((prev) => ({
        ...prev,
        customerName: "",
        email: "",
      }));
    }
    setIsAutoFilledCustomer(false);
    setSelectedCustomerOption(null);
    setActiveGuestField("customerName");
  }, [
    isAutoFilledCustomer,
    isLookingUpCustomer,
    matchedCustomerByMobile,
    mobileLookupValue,
    selectedCustomerOption,
    showGuestKeyboardModal,
  ]);

  const handleStep3Ok = () => {
    resetWizard();
    setShowReservationsList(true);
  };

  const closeGuestKeyboardModal = () => {
    setShowReservationConfirmModal(false);
    setShowGuestKeyboardModal(false);
    clearReservationEditorState();
    if (reservationOverlayContext === "list") {
      setShowReservationsList(true);
    }
  };

  const renderBackButton = (onClick) => (
    <Button variant="secondary" size="sm" className="min-w-[96px]" onClick={onClick}>
      Back
    </Button>
  );

  const reservationListColumns = [
    { key: "code", label: "Code" },
    { key: "customer", label: "Customer" },
    { key: "mobile", label: "Mobile No." },
    { key: "date", label: "Date" },
    { key: "time", label: "Time Slot" },
    { key: "guest", label: "Guests" },
    { key: "table", label: "Table" },
    { key: "status", label: "Status" },
    {
      key: "action",
      label: "Action",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (row) => (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="min-w-[80px]"
            onClick={() => openReservationEditor(row.reservation)}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="primary"
            className="min-w-[112px]"
            onClick={() => openAssignTableModal(row.reservation)}
          >
            {row.tableId ? "Change Table" : "Assign Table"}
          </Button>
        </div>
      ),
    },
  ];

  const reservationListRows = filteredReservations.map((row) => ({
    id: row.reservationId,
    code: row.reservationCode,
    date: row.reservationDate,
    time: toDisplayTime(row.slotTime),
    guest: row.guestCount,
    customer: row.customerName || "-",
    mobile: row.mobile || row.mobileE164 || "-",
    table: row.tableName || row.tableNo || "-",
    status: row.status,
    tableId: row.tableId || row.assignedTableId || row.table?.tableId || "",
    reservation: row,
  }));

  const pageContent = shouldShowReservationsList ? (
    <div className={pageShellClass}>
      <ReservationsHeader
        title="All Reservations"
        titleClassName="text-xl sm:text-xl lg:text-xl"
        actions={renderBackButton(() => {
          setShowReservationsList(false);
          setReservationOverlayContext("slots");
        })}
      />

      <Card className="flex h-full min-h-0 flex-col rounded-[24px] p-5 sm:p-6">
        <div className="grid gap-3 border-b border-slate-200 pb-4 md:grid-cols-5 dark:border-slate-700">
          <Input
            label="From"
            type="date"
            value={listStartDate}
            className="h-9 px-3 text-sm placeholder:text-sm"
            max={listEndDate || undefined}
            onChange={(e) => handleListStartDateChange(e.target.value)}
          />
          <Input
            label="To"
            type="date"
            value={listEndDate}
            className="h-9 px-3 text-sm placeholder:text-sm"
            min={listStartDate || undefined}
            onChange={(e) => handleListEndDateChange(e.target.value)}
          />
          <Select
            label="Status"
            value={listStatus}
            className="h-9 px-3 text-sm"
            onChange={(e) => setListStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="CONFIRMED">CONFIRMED</option>
            <option value="CHECKED_IN">CHECKED_IN</option>
            <option value="USED">USED</option>
            <option value="NO_SHOW">NO_SHOW</option>
            <option value="CANCELLED">CANCELLED</option>
          </Select>
          <Input
            label="Search"
            placeholder="Code / customer / mobile"
            value={listSearch}
            className="h-9 px-3 text-sm placeholder:text-sm"
            onChange={(e) => setListSearch(e.target.value)}
          />
          <div className="flex items-end">
            <Button
              variant="secondary"
              size="sm"
              className="h-9 min-w-[104px] rounded-xl px-3 text-sm"
              disabled={isRefreshingReservations}
              onClick={() => {
                setListStartDate("");
                setListEndDate("");
                setListStatus("");
                setListSearch("");
                setListDateError("");
                setListRefreshKey((prev) => prev + 1);
              }}
            >
              {isRefreshingReservations ? "Clearing..." : "Clear"}
            </Button>
          </div>
        </div>

        {listDateError ? <Alert tone="error" className="mt-3 py-2.5">{listDateError}</Alert> : null}
        {reservationListNotice ? (
          <Alert
            tone={reservationListNotice.tone}
            className="mt-3 py-2.5"
            onClose={() => setReservationListNotice(null)}
          >
            {reservationListNotice.message}
          </Alert>
        ) : null}

        <div className="mt-4 min-h-0 flex-1 overflow-auto">
          <Table
            compact
            className="min-h-0"
            columns={reservationListColumns}
            rows={reservationListRows}
            emptyText="No reservations found."
          />
        </div>
      </Card>
    </div>
  ) : (
    step === 1 && (
      <ReservationsHeader
        title="Select a Reservation Slot"
        titleClassName="text-xl sm:text-xl lg:text-xl"
        actions={(
          <Button
            variant="secondary"
            size="sm"
            className="inline-flex min-w-[180px] items-center justify-center gap-2 self-start rounded-xl px-4"
            onClick={() => {
              setShowReservationsList(true);
              setReservationOverlayContext("list");
            }}
          >
            <ListIcon className="h-4 w-4" />
            View Reservations List
          </Button>
        )}
      />
    )
  );

  return (
    <div className={`${pageShellClass} min-h-0`}>
      {pageContent}
      {!shouldShowReservationsList && step === 1 && (
        <div className="flex min-h-0 flex-col gap-3 lg:h-[calc(100vh-9rem)] lg:max-h-[calc(100vh-9rem)]">
          <Card className="flex flex-1 min-h-0 overflow-hidden rounded-[24px] p-4 sm:p-5">
            <div className="grid flex-1 min-h-0 gap-3 xl:grid-cols-[minmax(220px,0.52fr)_minmax(0,1.48fr)] xl:items-stretch">
              <section className="space-y-2.5 xl:flex xl:h-full xl:min-h-0 xl:flex-col xl:border-r xl:border-slate-200 xl:pr-4">
                <div className="flex items-center justify-center gap-2.5">
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-indigo-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50"
                    onClick={() => setMonthOffset((prev) => prev - 1)}
                    aria-label="Previous month"
                  >
                    <ArrowLeftIcon className="h-3 w-3" />
                  </button>
                  <div className="min-w-[126px] text-center text-[14px] font-semibold tracking-[-0.02em] text-slate-900 sm:text-[15px]">
                    {calendarModel.monthLabel}
                  </div>
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-indigo-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50"
                    onClick={() => setMonthOffset((prev) => prev + 1)}
                    aria-label="Next month"
                  >
                    <ArrowRightIcon className="h-3 w-3" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center">
                  {weekDays.map((day) => (
                    <div key={day} className="pb-1 text-[9px] font-bold tracking-wide text-slate-500 sm:text-[10px]">
                      {day}
                    </div>
                  ))}

                  {calendarModel.cells.map((cell, index) => {
                    if (!cell) {
                      return (
                        <div
                          key={`empty-${index}`}
                          className={`h-[30px] rounded-[10px] ${dayToneClasses.inactive} sm:h-[32px]`}
                        />
                      );
                    }

                    const isSelected = selectedDate === cell.iso;
                    const isPastDate = cell.iso < today;

                    return (
                      <button
                        key={cell.iso}
                        type="button"
                        disabled={isPastDate}
                        className={[
                          "h-[30px] rounded-[10px] border px-1 text-[11px] font-semibold transition-all duration-200 sm:h-[32px] sm:text-[12px]",
                          isSelected
                            ? dayToneClasses.selected
                            : isPastDate
                              ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
                              : dayToneClasses.available,
                        ].join(" ")}
                        onClick={() => {
                          if (isPastDate) return;
                          setSelectedDate(cell.iso);
                          setSelectedTime("");
                          setStep1Error("");
                        }}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5 text-[10px] font-medium text-slate-600">
                  <div className="flex items-center gap-2">
                    <DotIcon className="h-2.5 w-2.5 text-emerald-500" />
                    Available
                  </div>
                  <div className="flex items-center gap-2">
                    <DotIcon className="h-2.5 w-2.5 text-amber-500" />
                    Limited
                  </div>
                  <div className="flex items-center gap-2">
                    <DotIcon className="h-2.5 w-2.5 text-rose-500" />
                    Fully Booked
                  </div>
                  <div className="flex items-center gap-2">
                    <DotIcon className="h-2.5 w-2.5 text-slate-400" />
                    Unavailable
                  </div>
                </div>
              </section>

              <section className="flex min-h-0 flex-col space-y-2 xl:h-full xl:overflow-hidden">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-3 text-slate-900">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
                      <ClockIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[15px] font-semibold sm:text-[16px]">{selectedDayLabel}</div>
                     
                    </div>
                  </div>
                </div>

                {isSlotsPending ? (
                  <ReservationSlotsLoadingState />
                ) : timeSlots.length > 0 ? (
                  <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1 py-1">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                      {timeSlots.map((slot) => {
                        const booked = bookedCountBySlot.get(slot) || 0;
                        const slotMeta = slotByTime.get(slot);
                        const slotState = getSlotState(slotMeta, booked);
                        const availableSeats = Number(slotMeta?.availableSeats ?? 0);
                        const isSelected = selectedTime === slot;
                        const isDisabled = slotState.tone === "full" || slotState.tone === "unavailable";

                        return (
                          <button
                            key={slot}
                            type="button"
                            disabled={isDisabled}
                            className={[
                              "w-full min-w-0 min-h-[52px] rounded-[14px] border px-2.5 py-1.5 text-left transition-all duration-200",
                              slotToneClasses[slotState.tone],
                              isSelected
                                ? "ring-2 ring-indigo-500 ring-offset-1 ring-offset-white shadow-[0_12px_22px_rgba(79,70,229,0.12)]"
                                : "shadow-[0_6px_14px_rgba(15,23,42,0.05)]",
                              isDisabled ? "cursor-not-allowed opacity-90" : "hover:-translate-y-0.5",
                            ].join(" ")}
                            onClick={() => {
                              setSelectedTime(slot);
                              startDetailsStep(slot);
                            }}
                          >
                            <div className="text-[11px] font-semibold tracking-[-0.02em] text-slate-900 sm:text-[12px]">
                              {toDisplayTime(slot)}
                            </div>
                            <div className={`mt-0.5 flex items-center gap-1 text-[9px] font-semibold sm:text-[10px] ${slotIndicatorClasses[slotState.tone]}`}>
                              {slotState.tone === "available" && <DotIcon className="h-2.5 w-2.5" />}
                              {slotState.tone === "limited" && <WarningIcon className="h-3 w-3" />}
                              {slotState.tone === "full" && <XCircleIcon className="h-3 w-3" />}
                              {slotState.tone === "unavailable" && <DotIcon className="h-2.5 w-2.5" />}
                              <span>{slotState.label}</span>
                              {slotState.tone === "available" && availableSeats > 0 ? (
                                <span>{availableSeats}</span>
                              ) : null}
                            </div>
                            <div className="mt-0.5 text-[9px] text-slate-500">
                              <span>{slotState.detail}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                    No reservation slots are available for the selected date.
                  </div>
                )}

                {step1Error ? <Alert tone="error">{step1Error}</Alert> : null}
              </section>
            </div>
          </Card>
        </div>
      )}

      <Modal
        isOpen={showGuestKeyboardModal}
        onClose={closeGuestKeyboardModal}
        size="xl"
        align="center"
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <Input
                  ref={mobileInputRef}
                  label="Mobile"
                  required
                  value={form.mobile}
                  placeholder={guestFieldMeta.mobile.placeholder}
                  onChange={(e) => updateGuestFieldValue("mobile", e.target.value)}
                  onClick={() => focusGuestField("mobile")}
                  onFocus={() => focusGuestField("mobile")}
                  error={step2FieldErrors.mobile || null}
                  className={
                    step2FieldErrors.mobile
                      ? "cursor-text caret-slate-900 ring-2 ring-rose-500 dark:caret-slate-100 dark:ring-rose-400"
                      : activeGuestField === "mobile"
                        ? "cursor-text caret-slate-900 ring-2 ring-indigo-500 dark:caret-slate-100 dark:ring-indigo-400"
                        : "cursor-text caret-slate-900 dark:caret-slate-100"
                  }
                />
              </div>
              <Input
                label="Customer Name"
                required
                value={form.customerName}
                placeholder={guestFieldMeta.customerName.placeholder}
                onChange={(e) => updateGuestFieldValue("customerName", e.target.value)}
                onMouseDown={(e) => blockGuestFieldSwitch(e, "customerName")}
                onPointerDown={(e) => blockGuestFieldSwitch(e, "customerName")}
                onClick={() => focusGuestField("customerName")}
                onFocus={() => focusGuestField("customerName")}
                error={step2FieldErrors.customerName || null}
                className={
                  step2FieldErrors.customerName
                    ? "cursor-text caret-slate-900 ring-2 ring-rose-500 dark:caret-slate-100 dark:ring-rose-400"
                    : activeGuestField === "customerName"
                      ? "cursor-text caret-slate-900 ring-2 ring-indigo-500 dark:caret-slate-100 dark:ring-indigo-400"
                      : "cursor-text caret-slate-900 dark:caret-slate-100"
                }
              />
              <Input
                label="Email"
                value={form.email}
                placeholder={guestFieldMeta.email.placeholder}
                onChange={(e) => updateGuestFieldValue("email", e.target.value)}
                onMouseDown={(e) => blockGuestFieldSwitch(e, "email")}
                onPointerDown={(e) => blockGuestFieldSwitch(e, "email")}
                onClick={() => focusGuestField("email")}
                onFocus={() => focusGuestField("email")}
                className={
                  activeGuestField === "email"
                    ? "cursor-text caret-slate-900 ring-2 ring-indigo-500 dark:caret-slate-100 dark:ring-indigo-400"
                    : "cursor-text caret-slate-900 dark:caret-slate-100"
                }
              />
              <label className="flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-400">
                Available Sitting
                <div className="flex h-11 w-full cursor-not-allowed items-center rounded-lg border border-slate-200 bg-slate-50 px-4 text-base text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                  <span>
                    {selectedStep2AvailableSeats > 0
                      ? `${selectedStep2AvailableSeats} seats available`
                      : selectedStep2SlotState.label}
                  </span>
                </div>
              </label>
            </div>
            <button
              type="button"
              onClick={closeGuestKeyboardModal}
              className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-300"
              aria-label="Close"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label className="flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span>
                  Date
                  {editingReservationId ? (
                    <span className="ml-1 text-rose-600">*</span>
                  ) : null}
                </span>
                {editingReservationId ? (
                <DatePicker
                  selected={parseIsoDate(form.reservationDate)}
                  onChange={handleReservationDateChange}
                  minDate={parseIsoDate(today)}
                  dateFormat="EEEE, MMMM d"
                  customInput={<ReservationDateInput />}
                  popperClassName="z-[70]"
                />
              ) : (
                <div className="flex h-11 w-full cursor-not-allowed items-center rounded-lg border border-slate-200 bg-slate-50 px-4 text-base text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                  {modalDateLabel}
                </div>
              )}
            </label>
              <label className="flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span>
                  Time Slot
                  {editingReservationId ? (
                    <span className="ml-1 text-rose-600">*</span>
                  ) : null}
                </span>
                {editingReservationId ? (
                <select
                  value={form.slotTime}
                  disabled={isSlotsPending}
                  onChange={(event) => handleReservationTimeChange(event.target.value)}
                  className="h-11 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-base text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100"
                >
                  <option value="">
                    {isSlotsPending ? "Loading slots..." : "Select time slot"}
                  </option>
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {toDisplayTime(slot)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex h-11 w-full cursor-not-allowed items-center rounded-lg border border-slate-200 bg-slate-50 px-4 text-base text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                  {toDisplayTime(form.slotTime)}
                </div>
              )}
            </label>
            <Input
              label="Guests"
              required
              value={form.guestCount === "0" ? "" : form.guestCount}
              placeholder={guestFieldMeta.guestCount.placeholder}
              onChange={(e) => updateGuestFieldValue("guestCount", e.target.value)}
              onMouseDown={(e) => blockGuestFieldSwitch(e, "guestCount")}
              onPointerDown={(e) => blockGuestFieldSwitch(e, "guestCount")}
              onClick={() => focusGuestField("guestCount")}
              onFocus={() => focusGuestField("guestCount")}
              error={step2FieldErrors.guestCount || null}
              className={
                step2FieldErrors.guestCount
                  ? "cursor-text caret-slate-900 ring-2 ring-rose-500 dark:caret-slate-100 dark:ring-rose-400"
                  : activeGuestField === "guestCount"
                    ? "cursor-text caret-slate-900 ring-2 ring-indigo-500 dark:caret-slate-100 dark:ring-indigo-400"
                    : "cursor-text caret-slate-900 dark:caret-slate-100"
              }
            />
          </div>

          <OnScreenKeyboard
            layout={guestFieldMeta[activeGuestField]?.layout || "alpha"}
            size="md"
            value={getActiveGuestValue()}
            onChange={setActiveGuestValue}
            onDone={handleGuestKeyboardDone}
            showDone={false}
            title={guestFieldMeta[activeGuestField]?.label || "Guest Details"}
          />

          {step2Error ? <Alert tone="error" className="py-2.5">{step2Error}</Alert> : null}

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
            <Button
              size="sm"
              variant="secondary"
              onClick={closeGuestKeyboardModal}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreateOrUpdate}
              disabled={createState.isLoading || updateState.isLoading}
            >
              {createState.isLoading || updateState.isLoading
                ? (editingReservationId ? "Updating..." : "Creating...")
                : (editingReservationId ? "Update Reservation" : "Create Reservation")}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showReservationConfirmModal}
        onClose={closeReservationConfirmModal}
        title="Confirm Reservation"
        size="md"
        align="center"
        zIndexClassName="z-[60]"
        onConfirm={handleCreateOrUpdate}
        confirmText="Confirm Reservation"
        cancelText="Cancel"
        isLoading={createState.isLoading}
      >
        <p className="text-sm leading-6 text-slate-600 dark:text-slate-400">
          Are you sure you want to confirm this reservation?
        </p>
      </Modal>

      <Modal
        isOpen={Boolean(assigningReservation)}
        onClose={closeAssignTableModal}
        title="Assign Table"
        size="xl"
        align="center"
        panelClassName="min-h-[500px]"
        onConfirm={handleConfirmAssignTable}
        confirmText="Confirm Assignment"
        cancelText="Cancel"
        isLoading={assignTableState.isLoading}
      >
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-900/30">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  Reservation Code
                </div>
                <div className="mt-1 text-[15px] font-semibold text-slate-900 dark:text-slate-100">
                  {assigningReservation?.reservationCode || "-"}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  Customer
                </div>
                <div className="mt-1 text-[15px] font-semibold text-slate-900 dark:text-slate-100">
                  {assigningReservation?.customerName || "-"}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  Guests
                </div>
                <div className="mt-1 text-[15px] font-semibold text-slate-900 dark:text-slate-100">
                  {assigningReservation?.guestCount || "-"}
                </div>
              </div>
            </div>
          </div>

          {assignTableError ? (
            <Alert tone="error" className="py-2.5" onClose={() => setAssignTableError("")}>
              {assignTableError}
            </Alert>
          ) : null}

          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Available Tables
                </div>
              
              </div>
              {isFetchingTables ? (
                <span className="text-xs font-medium text-indigo-600">Refreshing...</span>
              ) : null}
            </div>

            {isLoadingTables ? (
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4 text-sm text-indigo-800">
                Loading available tables...
              </div>
            ) : assignableTables.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-300">
                No available tables found right now.
              </div>
            ) : (
              <div className="max-h-[320px] overflow-y-auto pr-1">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {assignableTables.map((table) => {
                  const tableId = getTableId(table);
                  const selected = String(selectedAssignTableId) === String(tableId);
                  const tableBackground = getAssignTableBackground(table);

                  return (
                    <button
                      key={tableId}
                      type="button"
                      onClick={() => {
                        setSelectedAssignTableId(tableId);
                        setAssignTableError("");
                      }}
                      className={[
                        "relative flex min-h-[76px] w-full items-center justify-center rounded-2xl border-2 px-3 py-2.5 text-center transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-slate-100 dark:focus:ring-indigo-500 dark:focus:ring-offset-slate-900",
                        selected
                          ? "border-indigo-500 ring-2 ring-indigo-100 dark:border-indigo-400 dark:ring-indigo-900/50"
                          : "border-slate-200 dark:border-slate-700",
                      ].join(" ")}
                      style={{
                        backgroundColor: tableBackground,
                        boxShadow: selected
                          ? "0 18px 38px -28px rgba(79, 70, 229, 0.45)"
                          : "0 18px 38px -30px rgba(15, 23, 42, 0.22)",
                      }}
                    >
                      <div className="absolute right-2 top-2">
                        <span
                          className={[
                            "block h-3 w-3 rounded-full shadow-lg ring-[3px]",
                            selected
                              ? "bg-indigo-600 ring-indigo-100"
                              : "bg-slate-950 ring-slate-200",
                          ].join(" ")}
                        />
                      </div>
                      <span className="truncate text-xl font-black tracking-tight text-slate-950">
                        {getTableLabel(table)}
                      </span>
                    </button>
                  );
                })}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showSuccessModal}
        onClose={handleStep3Ok}
        title="Reservation Confirmed"
        size="md"
        showCancel={false}
        align="center"
        backdropClassName="bg-slate-950/45 backdrop-blur-[2px]"
      >
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shadow-sm dark:bg-emerald-900/30 dark:text-emerald-300">
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mt-3 text-[18px] font-semibold text-slate-900 dark:text-slate-100">
              Reservation created successfully
            </p>
           
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 dark:border-slate-700 dark:bg-slate-900/30">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Code</div>
              <div className="mt-1 text-[16px] font-semibold text-slate-900 dark:text-slate-100">
                {createdReservation?.reservationCode || "-"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 dark:border-slate-700 dark:bg-slate-900/30">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Date</div>
              <div className="mt-1 text-[16px] font-semibold text-slate-900 dark:text-slate-100">
                {createdReservation?.reservationDate || form.reservationDate}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 dark:border-slate-700 dark:bg-slate-900/30">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Time Slot</div>
              <div className="mt-1 text-[16px] font-semibold text-slate-900 dark:text-slate-100">
                {toDisplayTime(createdReservation?.slotTime || form.slotTime)}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3.5 dark:border-slate-700 dark:bg-slate-900/30">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Guests</div>
              <div className="mt-1 text-[16px] font-semibold text-slate-900 dark:text-slate-100">
                {createdReservation?.guestCount || form.guestCount}
              </div>
            </div>
          </div>

          <div className="flex justify-center border-t border-slate-200 pt-4 dark:border-slate-700">
            <Button
              size="sm"
              className="min-w-[120px]"
              onClick={handleStep3Ok}
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Reservations;
